import React, { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { Box, Chip, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CustomPagination from '../../../components/customPagination';
import { useGetOrganizationsHandler } from '../dataHandlers/getOrganizationsHandler';
import OrganizationActionMenu from './OrganizationActionMenu';
import ProvisionTINModal from './ProvisionTINModal';
import { useVendorFeatures } from '../../../hooks/useVendorFeatures';
import { isTinProvisionUiEnabled } from '../../../config/appConfig';
import { useProvisionTINHandler } from '../dataHandlers/provisionTINHandler';

const OrganizationsTable = ({
  handlePaginationChange,
  searchQuery = {},
  paginationModel = { page: 0, pageSize: 10 },
}) => {
  const { organizations, totalDocuments, isLoading } = useGetOrganizationsHandler({ searchQuery });
  const { getEnabledFeatureLabels } = useVendorFeatures();
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const showProvisionTIN = isTinProvisionUiEnabled();

  const { onSubmit, isPending } = useProvisionTINHandler({
    onSuccess: () => {
      setSelectedOrganization(null);
    },
  });

  const handleCopyId = async (value) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      // Ignore clipboard errors to avoid blocking table interactions.
    }
  };

  const renderCopyableIdCell = (value, to) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%', height: '100%' }}>
      <Link
        component={RouterLink}
        to={to}
        underline="hover"
        variant="body2"
        title={value}
        sx={{
          minWidth: 0,
          flexGrow: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Link>
      <Tooltip title="Copy ID">
        <span>
          <IconButton
            size="small"
            color="primary"
            sx={{ ml: 0.5, flexShrink: 0 }}
            aria-label={`copy-${value || 'id'}`}
            disabled={!value}
            onClick={(event) => {
              event.stopPropagation();
              handleCopyId(value);
            }}
          >
            <ContentCopy fontSize="inherit" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  const columns = [
    {
      field: 'stripe_customer_id',
      headerName: 'Stripe Customer ID',
      flex: 0.75,
      renderCell: (params) => renderCopyableIdCell(params.value, `/organizations/${params.value}`),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 0.75,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 0.75,
    },
    {
      field: 'contact_emails',
      headerName: 'Contact Emails',
      flex: 0.75,
      valueGetter: (params) => {
        return Array.isArray(params) ? params.join(', ') : '';
      },
    },
    {
      field: 'features',
      headerName: 'Features',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => {
        const labels = getEnabledFeatureLabels(params.row?.features);

        if (labels.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }

        return (
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ py: 0.5, width: '100%' }}>
            {labels.map((label) => (
              <Chip key={label} label={label} size="small" color="primary" variant="outlined" />
            ))}
          </Stack>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.25,
      sortable: false,
      renderCell: (params) => {
        return (
          <OrganizationActionMenu
            organization={params.row}
            onProvisionTIN={setSelectedOrganization}
            showProvisionTIN={showProvisionTIN}
          />
        );
      },
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={organizations}
        columns={columns}
        getRowId={(row) => row.stripe_customer_id}
        getRowHeight={() => 'auto'}
        paginationMode="server"
        sx={{
          '& .MuiDataGrid-cell': {
            alignItems: 'flex-start',
          },
        }}
        rowCount={totalDocuments}
        loading={isLoading}
        paginationModel={paginationModel}
        pageSizeOptions={[10, 25]}
        disableColumnMenu
        disableColumnSorting
        disableColumnFilter
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => {
          handlePaginationChange(model.page, model.pageSize);
        }}
        slots={{
          pagination: CustomPagination,
        }}
      />

      {showProvisionTIN ? (
        <ProvisionTINModal
          open={Boolean(selectedOrganization)}
          organization={selectedOrganization}
          onClose={() => setSelectedOrganization(null)}
          onSubmit={onSubmit}
          isLoading={isPending}
        />
      ) : null}
    </Box>
  );
};

export default OrganizationsTable;
